import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import CallEndIcon from "@mui/icons-material/CallEnd";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DownloadIcon from "@mui/icons-material/Download";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import RefreshIcon from "@mui/icons-material/Refresh";

import PrimarySearchAppBar from "../../Components/Layout/Header";
import Footer from "../../Components/Home/Footer";
import DeviceDetailsCard from "../../Components/Device/DeviceDetailsCard";
import DeviceMap from "../../Components/Device/DeviceMap";
import LiveStreamPanel from "../../Components/Device/LiveStreamPanel";
import AppContext from "../../../context/app-context";
import { formatDurationFromMs, formatIstDateTime } from "../../../utils/time";

const backendUrl = (process.env.REACT_APP_URL || "").replace(/\/$/, "");
const freepbxUrl = process.env.REACT_APP_FREEPBX_URL || "http://20.62.249.124";
const fallbackDeviceExtension = process.env.REACT_APP_DEVICE_EXTENSION || "1100";
const fallbackTargetExtension = process.env.REACT_APP_TARGET_EXTENSION || "1101";
const sipDialDomain = process.env.REACT_APP_SIP_DIAL_DOMAIN || "20.62.249.124";

const STEP_COLORS = {
  complete: "#16a34a",
  active: "#2563eb",
  failed: "#dc2626",
  skipped: "#94a3b8",
  waiting: "#e2e8f0",
};

const STEP_TEXT_COLORS = {
  complete: "#475569",
  active: "#1d4ed8",
  failed: "#b91c1c",
  skipped: "#94a3b8",
  waiting: "#94a3b8",
};

function toBackendUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${backendUrl}${path.startsWith("/") ? path : `/${path}`}`;
}

function buildSipUri(extension) {
  const cleanExtension = encodeURIComponent(String(extension || "").trim());
  if (!cleanExtension) return "";
  return sipDialDomain ? `sip:${cleanExtension}@${sipDialDomain}` : `sip:${cleanExtension}`;
}

function openProtocolUri(uri) {
  const anchor = document.createElement("a");
  anchor.href = uri;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  window.setTimeout(() => {
    if (anchor.parentNode) {
      anchor.parentNode.removeChild(anchor);
    }
  }, 500);
}

function formatSeconds(value, fallback = "Waiting") {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return fallback;
  return formatDurationFromMs(seconds * 1000, fallback);
}

function formatTalkTime(call) {
  if (!call?.answered) return "0 sec";
  return formatSeconds(call.billsec, "0 sec");
}

function getRecordingName(recording) {
  return recording?.name || recording?.recording_file || recording?.cdr?.recording_file || "FreePBX recording";
}

function getStepIcon(step) {
  if (step.state === "failed") return <ErrorOutlineIcon fontSize="small" />;
  if (step.state === "skipped" || step.state === "waiting") return <RadioButtonUncheckedIcon fontSize="small" />;
  if (step.key === "button") return <NotificationsActiveIcon fontSize="small" />;
  if (step.key === "call_placed" || step.key === "voice") return <PhoneInTalkIcon fontSize="small" />;
  if (step.key === "call_ended") return <CallEndIcon fontSize="small" />;
  if (step.key === "recording") return <AudiotrackIcon fontSize="small" />;
  return <CheckCircleIcon fontSize="small" />;
}

function stateChipColor(state) {
  if (state === "failed") return "error";
  if (state === "active") return "warning";
  if (state === "complete") return "success";
  return "default";
}

function InfoTile({ label, value, helper, icon, tone = "primary" }) {
  const iconColor = tone === "error" ? "#dc2626" : tone === "success" ? "#16a34a" : "#1d4ed8";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: "100%",
        borderRadius: 2,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        background: "#fff",
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box sx={{ color: iconColor, mt: 0.2 }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8 }}>
            {label}
          </Typography>
          <Typography variant="h6" sx={{ color: "#111827", fontWeight: 800, lineHeight: 1.2 }} noWrap>
            {value}
          </Typography>
          {helper ? (
            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.4 }}>
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function JourneyTimeline({ steps }) {
  return (
    <Stack spacing={1.35}>
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        const state = step.state || "waiting";
        const isActive = state === "active";
        const isFailed = state === "failed";
        const circleColor = STEP_COLORS[state] || STEP_COLORS.waiting;
        const iconColor = state === "waiting" || state === "skipped" ? "#94a3b8" : "#fff";

        return (
          <Box
            key={step.key}
            sx={{
              display: "flex",
              gap: 1.7,
              position: "relative",
              p: isActive || isFailed ? 1.25 : 0,
              ml: isActive || isFailed ? -1.25 : 0,
              borderRadius: 2,
              background: isActive
                ? "rgba(37, 99, 235, 0.08)"
                : isFailed
                  ? "rgba(220, 38, 38, 0.08)"
                  : "transparent",
              border: isActive || isFailed ? "1px solid rgba(15, 23, 42, 0.08)" : "1px solid transparent",
            }}
          >
            {!isLast ? (
              <Box
                sx={{
                  position: "absolute",
                  left: isActive || isFailed ? 27 : 15,
                  top: isActive || isFailed ? 48 : 35,
                  bottom: -12,
                  width: 2,
                  bgcolor: state === "complete" ? "rgba(22, 163, 74, 0.35)" : "rgba(148, 163, 184, 0.28)",
                }}
              />
            ) : null}
            <Box
              sx={{
                width: 34,
                height: 34,
                flex: "0 0 34px",
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: circleColor,
                color: iconColor,
                boxShadow: state !== "waiting" && state !== "skipped" ? "0 8px 18px rgba(15,23,42,0.14)" : "none",
                zIndex: 1,
              }}
            >
              {getStepIcon(step)}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, pb: 0.8 }}>
              <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ mb: 0.2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 900, color: "#111827" }}>
                    {step.label}
                  </Typography>
                  {isActive ? <Chip label="Current" color="warning" size="small" sx={{ fontWeight: 800 }} /> : null}
                  {isFailed ? <Chip label="Failed" color="error" size="small" sx={{ fontWeight: 800 }} /> : null}
                </Stack>
                <Typography variant="caption" sx={{ color: "#64748b", whiteSpace: "nowrap", mt: 0.4 }}>
                  {step.time ? formatIstDateTime(step.time) : state === "skipped" ? "Skipped" : "Waiting"}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ color: STEP_TEXT_COLORS[state] || "#64748b" }}>
                {step.detail}
              </Typography>
              {step.evidence ? (
                <Typography variant="caption" sx={{ color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8 }}>
                  Evidence: {step.evidence}
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}

function LatestRecordingPanel({ recording, currentCall }) {
  const mediaUrl = toBackendUrl(recording?.media_url || recording?.url);
  const callAnswered = Boolean(currentCall?.answered);
  const callFailed = currentCall && !callAnswered;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: "100%",
        borderRadius: 2,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="overline" sx={{ color: "#64748b", letterSpacing: 1.2 }}>
            Call Evidence
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 900, color: "#111827" }}>
            Latest Recording
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Audio appears here only when FreePBX confirms an answered call with a recording file.
          </Typography>
        </Box>
        <Chip
          label={recording ? "Recording ready" : callFailed ? "No recording" : "Waiting"}
          color={recording ? "success" : callFailed ? "error" : "default"}
          sx={{ alignSelf: { xs: "flex-start", sm: "center" }, fontWeight: 800 }}
        />
      </Stack>

      {recording ? (
        <Stack spacing={2}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              background: "linear-gradient(135deg, #0f172a, #1e293b)",
              color: "#fff",
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
              <AudiotrackIcon />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }} noWrap>
                  {getRecordingName(recording)}
                </Typography>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.68)" }}>
                  FreePBX verified call recording
                </Typography>
              </Box>
            </Stack>
            <audio controls preload="none" src={mediaUrl} style={{ width: "100%" }} />
          </Box>

          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <InfoTile label="Call Start" value={formatIstDateTime(currentCall?.start_time, "Unavailable")} icon={<PlayArrowIcon />} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <InfoTile label="Call End" value={formatIstDateTime(currentCall?.end_time, "Unavailable")} icon={<CallEndIcon />} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <InfoTile label="Talk Time" value={formatTalkTime(currentCall)} icon={<AccessTimeIcon />} />
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button href={mediaUrl} target="_blank" rel="noreferrer" variant="contained" startIcon={<DownloadIcon />}>
              Download Recording
            </Button>
            <Button href={mediaUrl} target="_blank" rel="noreferrer" variant="outlined" startIcon={<OpenInNewIcon />}>
              Open Raw File
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Box
          sx={{
            minHeight: 250,
            borderRadius: 2,
            border: callFailed ? "1px dashed rgba(220, 38, 38, 0.35)" : "1px dashed rgba(100, 116, 139, 0.35)",
            display: "grid",
            placeItems: "center",
            textAlign: "center",
            px: 3,
            color: "#64748b",
            background: callFailed ? "rgba(254, 242, 242, 0.62)" : "#fff",
          }}
        >
          <Box>
            {callFailed ? (
              <ErrorOutlineIcon sx={{ fontSize: 42, mb: 1, color: "#dc2626" }} />
            ) : (
              <AudiotrackIcon sx={{ fontSize: 42, mb: 1, color: "#94a3b8" }} />
            )}
            <Typography variant="h6" sx={{ fontWeight: 800, color: callFailed ? "#991b1b" : "#334155" }}>
              {callFailed ? "Call was not answered" : "Recording will appear here"}
            </Typography>
            <Typography variant="body2">
              {callFailed
                ? `FreePBX disposition: ${currentCall?.disposition || "UNKNOWN"}. No usable recording is expected.`
                : "After an answered call ends, FreePBX will close the file and the player will become active."}
            </Typography>
          </Box>
        </Box>
      )}
    </Paper>
  );
}

function CallHistoryTable({ calls }) {
  const rows = calls.slice(0, 15);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: "1px solid rgba(15, 23, 42, 0.08)",
        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="overline" sx={{ color: "#64748b", letterSpacing: 1.2 }}>
            FreePBX CDR
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 900, color: "#111827" }}>
            Call Logs and Recordings
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Newest CDR rows are shown first. Failed and unanswered calls are retained here too.
          </Typography>
        </Box>
        <Chip label={`${calls.length} item(s)`} variant="outlined" />
      </Stack>

      <TableContainer sx={{ borderRadius: 2, border: "1px solid rgba(15,23,42,0.08)" }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f8fafc" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 800 }}>Start Time</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>End Time</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Talk Time</TableCell>
              <TableCell sx={{ fontWeight: 800 }}>Recording</TableCell>
              <TableCell align="right" sx={{ fontWeight: 800 }}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length ? (
              rows.map((call) => {
                const recording = call.recording;
                const mediaUrl = toBackendUrl(recording?.media_url);
                const answered = Boolean(call.answered);

                return (
                  <TableRow key={call.uniqueid || `${call.start_time}-${call.src}-${call.dst}`} hover>
                    <TableCell>{formatIstDateTime(call.start_time, "Unavailable")}</TableCell>
                    <TableCell>{formatIstDateTime(call.end_time, "Unavailable")}</TableCell>
                    <TableCell>
                      <Chip
                        label={call.disposition || "UNKNOWN"}
                        color={answered ? "success" : "error"}
                        size="small"
                        sx={{ fontWeight: 800 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {formatTalkTime(call)}
                      </Typography>
                      {!answered ? (
                        <Typography variant="caption" sx={{ color: "#64748b" }}>
                          Attempt: {formatSeconds(call.duration, "0 sec")}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 360 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
                        {recording ? getRecordingName(recording) : "No usable recording"}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        {call.src} to {call.dst}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {answered && recording ? (
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button href={mediaUrl} target="_blank" rel="noreferrer" size="small" startIcon={<PlayArrowIcon />}>
                            Play
                          </Button>
                          <Button href={mediaUrl} target="_blank" rel="noreferrer" size="small" startIcon={<DownloadIcon />}>
                            Download
                          </Button>
                        </Stack>
                      ) : (
                        <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                          Not available
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5, color: "#64748b" }}>
                  No CDR rows found for this site yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function DevicePage() {
  const { siteCode } = useParams();
  const { addressData } = useContext(AppContext);
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveMode, setLiveMode] = useState("connecting");
  const [ringing, setRinging] = useState(false);
  const [actionMessage, setActionMessage] = useState("");
  const [sipHelpOpen, setSipHelpOpen] = useState(false);

  const fallbackSite = useMemo(
    () => addressData?.find?.((row) => row.site_code === siteCode) || null,
    [addressData, siteCode]
  );

  useEffect(() => {
    let mounted = true;
    let eventSource;
    let pollTimer;

    const applyJourneyPayload = (payload) => {
      const nextJourney = payload?.data || payload;
      if (!mounted || !nextJourney) return;
      setJourney(nextJourney);
      setLoading(false);
    };

    const loadJourney = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/v1/device/${siteCode}/journey`);
        applyJourneyPayload(response.data);
        setLiveMode("polling");
      } catch (error) {
        console.error("Unable to load device journey", error);
        if (mounted) {
          setLoading(false);
          setLiveMode("offline");
        }
      }
    };

    const startPolling = () => {
      if (pollTimer) return;
      loadJourney();
      pollTimer = window.setInterval(loadJourney, 1500);
    };

    if (window.EventSource) {
      eventSource = new EventSource(`${backendUrl}/api/v1/device/${siteCode}/journey/stream`);
      eventSource.addEventListener("device_journey", (event) => {
        try {
          applyJourneyPayload(JSON.parse(event.data));
          setLiveMode("streaming");
        } catch (error) {
          console.error("Invalid journey stream payload", error);
        }
      });
      eventSource.addEventListener("device_journey_error", () => {
        setLiveMode("polling");
        startPolling();
      });
      eventSource.onerror = () => {
        if (eventSource) eventSource.close();
        setLiveMode("polling");
        startPolling();
      };
    } else {
      startPolling();
    }

    return () => {
      mounted = false;
      if (eventSource) eventSource.close();
      if (pollTimer) window.clearInterval(pollTimer);
    };
  }, [siteCode]);

  const site = journey?.site || fallbackSite || {};
  const currentState = journey?.current_state || {
    label: loading ? "Loading" : "Ready",
    tone: loading ? "default" : "success",
    summary: loading ? "Connecting to the live journey feed." : "Monitoring for the next emergency button press.",
    state: "waiting",
  };
  const latestEvent = journey?.latest_event || null;
  const latestRecording = journey?.latest_recording || null;
  const currentCall = journey?.current_call || null;
  const callHistory = journey?.call_history || [];
  const steps = journey?.steps || [];
  const fromExtension = journey?.from_extension || latestEvent?.from_extension || fallbackDeviceExtension;
  const targetExtension = journey?.target_extension || latestEvent?.target_extension || fallbackTargetExtension;

  const handleRingDevice = async () => {
    const deviceExtension = fromExtension || fallbackDeviceExtension;
    const sipUri = buildSipUri(deviceExtension);

    if (!sipUri) {
      setActionMessage("Device extension is not available. Please check the site configuration.");
      return;
    }

    setRinging(true);
    setSipHelpOpen(false);
    setActionMessage(`Opening MicroSIP to call RPi extension ${deviceExtension} from operator extension ${targetExtension}.`);

    let externalAppLikelyOpened = false;
    const markExternalLaunch = () => {
      externalAppLikelyOpened = true;
    };

    window.addEventListener("blur", markExternalLaunch, { once: true });
    document.addEventListener("visibilitychange", markExternalLaunch, { once: true });
    openProtocolUri(sipUri);

    window.setTimeout(() => {
      setRinging(false);
      window.removeEventListener("blur", markExternalLaunch);
      document.removeEventListener("visibilitychange", markExternalLaunch);

      if (!externalAppLikelyOpened) {
        setSipHelpOpen(true);
        setActionMessage(
          `If MicroSIP did not open, configure MicroSIP as the default SIP app and manually dial ${deviceExtension}.`
        );
      }
    }, 1800);
  };

  const handleTelFallback = () => {
    const deviceExtension = fromExtension || fallbackDeviceExtension;
    openProtocolUri(`tel:${encodeURIComponent(deviceExtension)}`);
  };

  const shellStyle = useMemo(
    () => ({
      minHeight: "100vh",
      background:
        currentState.tone === "error"
          ? "radial-gradient(circle at top left, rgba(239,68,68,0.16), rgba(248,250,252,1) 30%, rgba(255,255,255,1) 100%)"
          : currentState.state === "active"
            ? "radial-gradient(circle at top left, rgba(59,130,246,0.14), rgba(248,250,252,1) 30%, rgba(255,255,255,1) 100%)"
            : "linear-gradient(180deg, rgba(248,250,252,1) 0%, rgba(255,255,255,1) 22%, rgba(255,255,255,1) 100%)",
      paddingBottom: "2rem",
    }),
    [currentState.tone, currentState.state]
  );

  return (
    <React.Fragment>
      <Helmet titleTemplate="PRSS | %s" title="Device" />
      <PrimarySearchAppBar />

      <div style={shellStyle}>
        <Container maxWidth="xl" sx={{ pt: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 3.5 },
              mb: 3,
              borderRadius: 2,
              border: "1px solid rgba(15, 23, 42, 0.08)",
              background: "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(239,246,255,0.82))",
              boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
            }}
          >
            <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} justifyContent="space-between">
              <Box>
                <Typography variant="overline" sx={{ color: "#64748b", letterSpacing: 1.4 }}>
                  Device Operations
                </Typography>
                <Typography variant="h3" sx={{ color: "#111827", fontWeight: 900, lineHeight: 1.05 }}>
                  Emergency Call Box
                </Typography>
                <Typography variant="body1" sx={{ color: "#475569", maxWidth: 830, mt: 1 }}>
                  Live camera, real FreePBX CDR state, answered/no-answer handling, and recording playback without
                  refreshing the browser.
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2.2 }}>
                  <Chip label={`Site: ${siteCode}`} color="primary" variant="outlined" />
                  <Chip label={site?.bus_stop || "CoreData Office"} variant="outlined" />
                  <Chip label={`Device ext ${fromExtension}`} variant="outlined" />
                  <Chip label={`Target ext ${targetExtension}`} variant="outlined" />
                  <Chip
                    label={liveMode === "streaming" ? "Live stream sync" : liveMode === "polling" ? "Live polling" : liveMode}
                    color={liveMode === "offline" ? "error" : "success"}
                    variant="outlined"
                  />
                </Stack>
              </Box>

              <Box sx={{ minWidth: { xs: "100%", md: 380 } }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: "1px solid rgba(15,23,42,0.08)",
                    background: "#fff",
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="caption" sx={{ color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>
                      Current Event
                    </Typography>
                    <Chip
                      label={currentState.label}
                      color={currentState.tone || stateChipColor(currentState.state)}
                      size="small"
                      sx={{ fontWeight: 800 }}
                    />
                  </Stack>
                  <Typography variant="body2" sx={{ color: "#475569", mb: 1.5 }}>
                    {currentState.summary}
                  </Typography>
                  <Divider sx={{ mb: 1.5 }} />
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        Last button press
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#111827" }}>
                        {formatIstDateTime(latestEvent?.timestamp || latestEvent?.ts, loading ? "Loading..." : "No button press yet")}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: "#64748b" }}>
                        Last sync
                      </Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#111827" }}>
                        {formatIstDateTime(journey?.generated_at, loading ? "Connecting..." : "Awaiting sync")}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            </Stack>
          </Paper>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <LiveStreamPanel />
            </Grid>

            <Grid item xs={12} lg={5}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: "100%",
                  borderRadius: 2,
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
                }}
              >
                <Stack direction="row" spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
                  <Box>
                    <Typography variant="overline" sx={{ color: "#64748b", letterSpacing: 1.2 }}>
                      Emergency Journey
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 900, color: "#111827" }}>
                      Button to Recording
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#64748b" }}>
                      The highlighted row shows where this incident currently is.
                    </Typography>
                  </Box>
                  <Chip
                    label={currentState.label}
                    color={currentState.tone || stateChipColor(currentState.state)}
                    icon={currentState.state === "complete" ? <CheckCircleIcon /> : undefined}
                    sx={{ alignSelf: "flex-start", fontWeight: 800 }}
                  />
                </Stack>

                <JourneyTimeline steps={steps} />

                <Divider sx={{ my: 2.5 }} />

                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <InfoTile label="Device Ext" value={fromExtension} helper="Raspberry Pi client" icon={<PhoneInTalkIcon />} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InfoTile label="Target Ext" value={targetExtension} helper="Operator phone" icon={<PhoneInTalkIcon />} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <InfoTile
                      label="Talk Time"
                      value={currentCall ? formatTalkTime(currentCall) : "Waiting"}
                      helper={currentCall?.disposition || "From FreePBX CDR"}
                      icon={<AccessTimeIcon />}
                      tone={currentCall && !currentCall.answered ? "error" : currentCall?.answered ? "success" : "primary"}
                    />
                  </Grid>
                </Grid>

                {journey?.integration_error ? (
                  <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: "rgba(254, 242, 242, 0.8)", color: "#991b1b" }}>
                    <Typography variant="body2">FreePBX live check error: {journey.integration_error}</Typography>
                  </Box>
                ) : null}

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2.5 }}>
                  <Button
                    onClick={handleRingDevice}
                    disabled={ringing}
                    variant="contained"
                    color="error"
                    startIcon={<PhoneInTalkIcon />}
                  >
                    {ringing ? "Ringing..." : "Ring Device"}
                  </Button>
                  <Button href={freepbxUrl} target="_blank" rel="noreferrer" variant="outlined" startIcon={<OpenInNewIcon />}>
                    Open FreePBX
                  </Button>
                  <Button href="/admin" variant="outlined" startIcon={<RefreshIcon />}>
                    View Telemetry
                  </Button>
                </Stack>
                {actionMessage ? (
                  <Typography variant="body2" sx={{ color: "#475569", mt: 1.5 }}>
                    {actionMessage}
                  </Typography>
                ) : null}

                <Dialog open={sipHelpOpen} onClose={() => setSipHelpOpen(false)} maxWidth="sm" fullWidth>
                  <DialogTitle sx={{ fontWeight: 900 }}>Configure MicroSIP to place this call</DialogTitle>
                  <DialogContent dividers>
                    <Stack spacing={1.5}>
                      <Typography variant="body2">
                        This button opens your local SIP phone using the SIP link for RPi extension{" "}
                        <strong>{fromExtension}</strong>. MicroSIP should be online with operator extension{" "}
                        <strong>{targetExtension}</strong>.
                      </Typography>
                      <Typography variant="body2">
                        If Chrome did not open MicroSIP, set MicroSIP as the default app for <strong>sip:</strong> links
                        in Windows, or choose MicroSIP when the browser asks which app should open SIP links.
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#64748b" }}>
                        Manual fallback: open MicroSIP and dial <strong>{fromExtension}</strong>.
                      </Typography>
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setSipHelpOpen(false)}>Close</Button>
                    <Button onClick={handleTelFallback} variant="outlined">
                      Try TEL fallback
                    </Button>
                    <Button onClick={handleRingDevice} variant="contained" color="error">
                      Try SIP again
                    </Button>
                  </DialogActions>
                </Dialog>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={5}>
              <LatestRecordingPanel recording={latestRecording} currentCall={currentCall} />
            </Grid>

            <Grid item xs={12} lg={7}>
              <CallHistoryTable calls={callHistory} />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="overline" sx={{ color: "#64748b", letterSpacing: 1.2 }}>
                Location and Device Information
              </Typography>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  p: 3,
                  borderRadius: 2,
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
                }}
              >
                <DeviceDetailsCard />
              </Paper>
            </Grid>

            <Grid item xs={12} lg={7}>
              <Paper
                elevation={0}
                sx={{
                  height: "100%",
                  overflow: "hidden",
                  borderRadius: 2,
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
                }}
              >
                <DeviceMap />
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </div>

      <Footer />
    </React.Fragment>
  );
}

export default DevicePage;
